import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { Achievement, AchievementProgress } from '../../types/achievement';
import ImageModal from '../common/ImageModal';


interface AchievementModalProps {
  achievement: Achievement;
  progress?: AchievementProgress | null;
  isCompleted?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

// Styled Components
const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: rgba(0, 25, 0, 0.95);
  border: 2px solid #00d4ff;
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #00d4ff;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: #ffffff;
  }
`;

const AchievementIcon = styled.div<{ $completed: boolean; $rarity: string }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${props => {
    if (props.$completed) {
      switch (props.$rarity.toLowerCase()) {
        case 'legendary': return 'linear-gradient(45deg, #ff6b35, #ff8c42)';
        case 'epic': return 'linear-gradient(45deg, #9b59b6, #8e44ad)';
        case 'rare': return 'linear-gradient(45deg, #3498db, #2980b9)';
        case 'uncommon': return 'linear-gradient(45deg, #27ae60, #229954)';
        default: return 'linear-gradient(45deg, #95a5a6, #7f8c8d)';
      }
    }
    return 'rgba(0, 212, 255, 0.2)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 48px;
  position: relative;
  box-shadow: ${props => props.$completed ? '0 0 20px rgba(0, 212, 255, 0.4)' : 'none'};
`;

const CompletedBadge = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 40px;
  height: 40px;
  background: #27ae60;
  border: 3px solid #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const AchievementName = styled.h2`
  font-family: 'Courier New', monospace;
  color: #ffffff;
  text-align: center;
  margin: 0 0 10px 0;
  font-size: 24px;
  font-weight: bold;
`;

const AchievementRarity = styled.div<{ $rarity: string }>`
  font-family: 'Courier New', monospace;
  text-align: center;
  margin-bottom: 20px;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 20px;
  display: inline-block;
  width: 100%;
  text-transform: uppercase;
  font-weight: bold;
  background: ${props => {
    switch (props.$rarity.toLowerCase()) {
      case 'legendary': return '#ff6b35';
      case 'epic': return '#9b59b6';
      case 'rare': return '#3498db';
      case 'uncommon': return '#27ae60';
      default: return '#95a5a6';
    }
  }};
  color: #ffffff;
`;

const AchievementDescription = styled.p`
  font-family: 'Courier New', monospace;
  color: #b0b0b0;
  text-align: center;
  line-height: 1.6;
  margin: 0 0 25px 0;
  font-size: 16px;
`;

const RequirementsSection = styled.div`
  margin-bottom: 25px;
`;

const SectionTitle = styled.h3`
  font-family: 'Courier New', monospace;
  color: #00d4ff;
  font-size: 18px;
  margin: 0 0 15px 0;
  text-transform: uppercase;
  font-weight: bold;
`;

const RequirementItem = styled.div`
  font-family: 'Courier New', monospace;
  color: #ffffff;
  margin: 8px 0;
  padding: 8px 12px;
  background: rgba(0, 212, 255, 0.1);
  border-radius: 6px;
  font-size: 14px;
`;

const ProgressSection = styled.div`
  margin-bottom: 20px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  overflow: hidden;
  margin: 10px 0;
`;

const ProgressFill = styled.div<{ $progress: number; $rarity: string }>`
  height: 100%;
  background: ${props => {
    switch (props.$rarity.toLowerCase()) {
      case 'legendary': return 'linear-gradient(90deg, #ff6b35, #ff8c42)';
      case 'epic': return 'linear-gradient(90deg, #9b59b6, #8e44ad)';
      case 'rare': return 'linear-gradient(90deg, #3498db, #2980b9)';
      case 'uncommon': return 'linear-gradient(90deg, #27ae60, #229954)';
      default: return 'linear-gradient(90deg, #95a5a6, #7f8c8d)';
    }
  }};
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-family: 'Courier New', monospace;
  color: #ffffff;
  text-align: center;
  font-size: 16px;
  font-weight: bold;
`;

const CompletedInfo = styled.div`
  background: rgba(39, 174, 96, 0.2);
  border: 1px solid #27ae60;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  margin-top: 20px;
`;

const CompletedText = styled.div`
  font-family: 'Courier New', monospace;
  color: #27ae60;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const CompletedDate = styled.div`
  font-family: 'Courier New', monospace;
  color: #b0b0b0;
  font-size: 14px;
`;

const GameTypeTag = styled.div`
  display: inline-block;
  background: rgba(0, 212, 255, 0.2);
  color: #00d4ff;
  padding: 4px 12px;
  border-radius: 12px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: bold;
  margin-bottom: 15px;
`;

const AchievementModal: React.FC<AchievementModalProps> = ({
  achievement,
  progress,
  isCompleted = false,
  isOpen,
  onClose
}) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  if (!isOpen) return null;

  const achievementCompleted = isCompleted || progress?.isCompleted || false;
  
  const calculateProgress = (): number => {
    if (achievementCompleted) return 100;
    if (!progress) return 0;

    const { conditions } = achievement;
    const { progress: progressInfo } = progress;

    switch (conditions.type) {
      case 'win_games':
      case 'play_games':
      case 'win_mission':
      case 'play_mission':
        return Math.min(100, (progressInfo.dailyCount / conditions.count) * 100);
      case 'win_streak':
      case 'play_streak':
        return Math.min(100, (progressInfo.currentStreak / conditions.count) * 100);
      case 'daily_games':
        return Math.min(100, (progressInfo.dailyCount / conditions.count) * 100);
      case 'weekly_games':
        return Math.min(100, (progressInfo.weeklyCount / conditions.count) * 100);
      case 'complete_all_missions':
        return Math.min(100, (progressInfo.completedMissions.length / conditions.count) * 100);
      // Клановые достижения
      case 'clan_win_games':
      case 'clan_play_games':
        return Math.min(100, (progressInfo.dailyCount / conditions.count) * 100);
      case 'clan_win_games_streak':
      case 'clan_play_games_streak':
        return Math.min(100, (progressInfo.currentStreak / conditions.count) * 100);
      case 'clan_members_count':
        return Math.min(100, ((progressInfo as any).memberCount / conditions.count) * 100);
      case 'clan_complete_all_missions':
        return Math.min(100, (progressInfo.completedMissions.length / conditions.count) * 100);
      default:
        return 0;
    }
  };

  const getProgressText = (): string => {
    if (achievementCompleted) return 'Completed!';
    if (!progress) return `0 / ${achievement.conditions.count}`;

    const { conditions } = achievement;
    const { progress: progressInfo } = progress;

    switch (conditions.type) {
      case 'win_games':
        return `${progressInfo.dailyCount} / ${conditions.count} wins`;
      case 'play_games':
        return `${progressInfo.dailyCount} / ${conditions.count} games played`;
      case 'win_mission':
        return `${progressInfo.dailyCount} / ${conditions.count} missions won`;
      case 'play_mission':
        return `${progressInfo.dailyCount} / ${conditions.count} missions played`;
      case 'win_streak':
        return `${progressInfo.currentStreak} / ${conditions.count} win streak`;
      case 'play_streak':
        return `${progressInfo.currentStreak} / ${conditions.count} game streak`;
      case 'daily_games':
        return `${progressInfo.dailyCount} / ${conditions.count} today`;
      case 'weekly_games':
        return `${progressInfo.weeklyCount} / ${conditions.count} this week`;
      case 'complete_all_missions':
        return `${progressInfo.completedMissions.length} / ${conditions.count} missions`;
      // Клановые достижения
      case 'clan_win_games':
        return `${progressInfo.dailyCount} / ${conditions.count} clan wins`;
      case 'clan_play_games':
        return `${progressInfo.dailyCount} / ${conditions.count} clan games`;
      case 'clan_win_games_streak':
        return `${progressInfo.currentStreak} / ${conditions.count} clan win streak`;
      case 'clan_play_games_streak':
        return `${progressInfo.currentStreak} / ${conditions.count} clan game streak`;
      case 'clan_members_count':
        return `${(progressInfo as any).memberCount} / ${conditions.count} members`;
      case 'clan_complete_all_missions':
        return `${progressInfo.completedMissions.length} / ${conditions.count} clan missions`;
      default:
        return `0 / ${conditions.count}`;
    }
  };

  const getRequirementText = (): string => {
    const conditions = achievement.conditions;
    
    switch (conditions.type) {
      case 'win_games':
        return `Win ${conditions.count} ${achievement.gameType} games${conditions.consecutively ? ' in a row' : ''}`;
      case 'play_games':
        return `Play ${conditions.count} ${achievement.gameType} games${conditions.consecutively ? ' in a row' : ''}`;
      case 'win_streak':
        return `Achieve a ${conditions.count}-game win streak in ${achievement.gameType}`;
      case 'play_streak':
        return `Play ${conditions.count} games in a row in ${achievement.gameType}`;
      case 'daily_games':
        return `Play ${conditions.count} ${achievement.gameType} games in one day`;
      case 'weekly_games':
        return `Play ${conditions.count} ${achievement.gameType} games in one week`;
      case 'complete_all_missions':
        return `Complete ${conditions.count} different missions in ${achievement.gameType}`;
      default:
        return `Complete the requirements for ${achievement.name}`;
    }
  };

  const progressPercent = calculateProgress();

  const handleIconClick = () => {
    if (achievement.iconUrl) {
      setIsImageModalOpen(true);
    }
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  return createPortal(
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <GameTypeTag>{achievement.gameType}</GameTypeTag>
        
        <AchievementIcon 
          $completed={achievementCompleted} 
          $rarity={achievement.rarity}
          onClick={handleIconClick}
          style={{ cursor: achievement.iconUrl ? 'pointer' : 'default' }}
        >
          {achievementCompleted && <CompletedBadge>✓</CompletedBadge>}
          {achievement.iconUrl ? (
            <img 
              src={achievement.iconUrl} 
              alt={achievement.name} 
              style={{ width: '100%', height: '100%', borderRadius: '50%' }} 
            />
          ) : (
            achievementCompleted ? '🏆' : '🎯'
          )}
        </AchievementIcon>

        <AchievementName>{achievement.name}</AchievementName>
        <AchievementRarity $rarity={achievement.rarity}>
          {achievement.rarity} Achievement
        </AchievementRarity>
        <AchievementDescription>{achievement.description}</AchievementDescription>

        <RequirementsSection>
          <SectionTitle>Requirements</SectionTitle>
          <RequirementItem>{getRequirementText()}</RequirementItem>
          {achievement.conditions.timeframe && (
            <RequirementItem>Time limit: {achievement.conditions.timeframe}</RequirementItem>
          )}
        </RequirementsSection>

        {!achievementCompleted && (
          <ProgressSection>
            <SectionTitle>Progress</SectionTitle>
            <ProgressText>{getProgressText()}</ProgressText>
            <ProgressBar>
              <ProgressFill $progress={progressPercent} $rarity={achievement.rarity} />
            </ProgressBar>
          </ProgressSection>
        )}

        {achievementCompleted && (
          <CompletedInfo>
            <CompletedText>🎉 Achievement Unlocked!</CompletedText>
            {progress?.completedAt && (
              <CompletedDate>
                Completed on {new Date(progress.completedAt).toLocaleDateString()}
              </CompletedDate>
            )}
          </CompletedInfo>
        )}
      </Modal>
      
      <ImageModal
        isOpen={isImageModalOpen}
        imageUrl={achievement.iconUrl || ''}
        imageAlt={`${achievement.name} achievement icon`}
        title={`${achievement.name} Achievement`}
        onClose={closeImageModal}
      />
    </Overlay>,
    document.body
  );
};

export default AchievementModal;